/**
 * Kidify lists: several named Saved lists, Amazon style, with sharing.
 *
 * The lists themselves live in the shopper's browser (assets/kidify-shelf.js,
 * `lists`), like Saved items always have: no account, no app, nothing sent
 * anywhere. This file adds what shoppers see:
 *
 *   Toast    A heart tap saves to the default list, then "Saved to
 *            <list> · Undo · Change" shows for a few seconds. Save for
 *            later in the cart shows "Moved to <list> · Change".
 *   Items    Each saved product keeps the option the shopper picked (added
 *            to the cart as that exact variant), an optional note, and the
 *            price it was saved at ("Price dropped" at 5% or more off).
 *            "Add all to cart" adds what it can and names the rest. Cart
 *            lines added from a list carry a hidden "_From Kidify list"
 *            property, so orders show which list they came from.
 *   Picker   "Change" opens a dialog to tick the lists a product belongs
 *            in, or make a new list.
 *   Page     <kidify-lists> on /pages/saved: the lists down the side (a
 *            row of chips on a phone), the chosen list's products with Add
 *            to cart, Move and Remove, and Share / Rename / Make default /
 *            Delete for the list.
 *   Shared   A share link carries the list name and product handles in the
 *            URL (/pages/saved?list=Birthday&items=a,b,c). Opening it shows
 *            that list read-only, with Add to cart and "Save to my lists".
 *            It is a snapshot: edits after sharing need a new link.
 */

// assets/kidify-shelf.js loads first (layout/theme.liquid) and hands over its
// API on window.Kidify: importing it here would load a second copy, since
// Shopify serves each asset under a versioned URL.
const { shelf, lists, escapeHtml, sizedImage, money, HEART, SAVED_EVENT, fetchCard, miniCard, track } = window.Kidify || {};

/** A saved price this much lower (5% or more) shows "Price dropped". */
const DROP_SHARE = 0.05;
/** Hidden cart line property: the list an item was bought from. It shows
 * on the order in Shopify admin, but not to the shopper (leading "_"). */
const LIST_PROPERTY = '_From Kidify list';

const EVENT = 'kidify:shelf';
const SAVED_URL = () => document.documentElement.dataset.kidifySavedUrl || '/pages/saved';

/* ---------- Product data ---------- */

const productCache = new Map();

function fetchProduct(handle) {
  if (!productCache.has(handle)) {
    productCache.set(
      handle,
      fetch(`/products/${encodeURIComponent(handle)}.js`)
        .then((response) => {
          if (response.status === 404) return { gone: true };
          return response.ok ? response.json().then((product) => ({ product })) : {};
        })
        .catch(() => ({}))
    );
  }
  return productCache.get(handle);
}

/** Cart data (/products/x.js) plus the card from the product card template. */
const fetchBoth = (handle) =>
  Promise.all([fetchProduct(handle), fetchCard(handle)]).then(([data, view]) => ({ ...data, card: view.card, gone: data.gone || view.gone }));

function snapshot(product) {
  return {
    handle: product.handle,
    title: product.title,
    url: product.url || `/products/${product.handle}`,
    image: product.featured_image || '',
    price: money(product.price),
    vendor: product.vendor || '',
    savedCents: product.price,
  };
}

/* ---------- Add to cart ----------
 * Products with a single variant go straight in; anything with options
 * links to its page so the shopper can choose. The theme's cart icon and
 * drawer listen for Shopify's cart event, so it is dispatched the same way
 * the product form does. */

/** POST items to the cart, announcing it the way the product form does. */
async function postLines(items, source) {
  let deferred = null;
  let CartLinesUpdateEvent = null;
  try {
    ({ CartLinesUpdateEvent } = await import('@shopify/events'));
    deferred = CartLinesUpdateEvent.createPromise();
    source.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );
  } catch {
    /* events module unavailable: the add still works, the bubble catches up on the next page */
  }
  try {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    });
    const body = await response.json();
    const cart = await fetch('/cart.js').then((r) => r.json());
    deferred?.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, source: 'kidify-lists', itemCount: items.length, didError: !response.ok },
    });
    if (!response.ok) return { ok: false, message: body.description || body.message || 'Could not add' };
    return { ok: true };
  } catch (error) {
    deferred?.reject(error);
    return { ok: false, message: 'Could not add' };
  }
}

const singleVariant = (product) => product.variants.length === 1 || product.has_only_default_variant;

/**
 * The variant a list item adds to the cart: the option the shopper saved,
 * or the only one a single-variant product has. `null` with a reason when
 * the shopper still has to choose, or nothing is left to buy.
 */
function variantFor(product, entry) {
  if (!product) return { variant: null, reason: 'gone' };
  const saved = entry?.variant ? product.variants.find((v) => String(v.id) === String(entry.variant)) : null;
  if (saved) return saved.available ? { variant: saved, saved: true } : { variant: null, reason: 'soldout', saved: true };
  if (product.available === false) return { variant: null, reason: 'soldout' };
  if (singleVariant(product)) {
    const only = product.variants.find((v) => v.available);
    return only ? { variant: only } : { variant: null, reason: 'soldout' };
  }
  return { variant: null, reason: 'options' };
}

const lineFor = (variant, listName) => ({
  id: variant.id,
  quantity: 1,
  ...(listName ? { properties: { [LIST_PROPERTY]: listName } } : {}),
});

async function addToCart(product, source, { entry, listName } = {}) {
  const { variant, reason } = variantFor(product, entry);
  if (!variant) return { ok: false, message: reason === 'options' ? 'Choose an option first' : 'Sold out' };
  const result = await postLines([lineFor(variant, listName)], source);
  if (result.ok) track?.('to_cart', { handle: product.handle, list: listName || '' });
  return result;
}

/** Price drop against the price first saved at, or null. */
function priceDrop(product, entry) {
  const was = Number(entry?.savedCents);
  if (!product || !Number.isFinite(was) || was <= 0) return null;
  return product.price <= was * (1 - DROP_SHARE) ? { was, now: product.price } : null;
}

/* ---------- Name dialog (new list, rename) ---------- */

/** Resolves to the text, or null on Cancel. `optional` allows an empty answer (used to clear a note). */
function askName({ title, label, value = '', confirm, optional = false, max = 50 }) {
  return new Promise((resolve) => {
    const dialog = document.createElement('dialog');
    dialog.className = 'kidify-ldialog';
    dialog.innerHTML = `
      <form method="dialog" class="kidify-ldialog__form">
        <h2 class="kidify-ldialog__title">${escapeHtml(title)}</h2>
        <label class="kidify-ldialog__label">${escapeHtml(label)}
          <input class="kidify-ldialog__input" name="name" maxlength="${Number(max) || 50}" ${optional ? '' : 'required'} value="${escapeHtml(value)}" autocomplete="off">
        </label>
        <div class="kidify-ldialog__buttons">
          <button type="button" class="kidify-lbtn kidify-lbtn--ghost" value="cancel" data-cancel>Cancel</button>
          <button type="submit" class="kidify-lbtn kidify-lbtn--primary" value="ok">${escapeHtml(confirm)}</button>
        </div>
      </form>`;
    document.body.append(dialog);
    const input = dialog.querySelector('input');
    const close = (result) => {
      dialog.close();
      dialog.remove();
      resolve(result);
    };
    dialog.querySelector('[data-cancel]').addEventListener('click', () => close(null));
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      close(null);
    });
    dialog.querySelector('form').addEventListener('submit', (event) => {
      event.preventDefault();
      const name = input.value.trim();
      if (name || optional) close(name);
    });
    dialog.showModal();
    input.select();
  });
}

/* ---------- List picker ---------- */

function openPicker(handle) {
  const item = lists.entry(handle);
  const dialog = document.createElement('dialog');
  dialog.className = 'kidify-ldialog kidify-lpicker';
  document.body.append(dialog);

  const render = () => {
    const state = lists.all();
    dialog.innerHTML = `
      <div class="kidify-ldialog__form">
        <h2 class="kidify-ldialog__title">Save to list</h2>
        ${item ? `<p class="kidify-lpicker__product">${escapeHtml(item.title)}</p>` : ''}
        <ul class="kidify-lpicker__lists" role="list">
          ${state.lists
            .map((list) => {
              const checked = list.items.some((entry) => entry.handle === handle);
              return `<li><label class="kidify-lpicker__row">
                  <input type="checkbox" data-list="${escapeHtml(list.id)}" ${checked ? 'checked' : ''}>
                  <span class="kidify-lpicker__name">${escapeHtml(list.name)}</span>
                  <span class="kidify-lpicker__count">${list.items.length}</span>
                </label></li>`;
            })
            .join('')}
        </ul>
        <button type="button" class="kidify-lbtn kidify-lbtn--ghost kidify-lpicker__new" data-new>+ New list</button>
        <div class="kidify-ldialog__buttons">
          <a class="kidify-lbtn kidify-lbtn--ghost" href="${escapeHtml(SAVED_URL())}">View lists</a>
          <button type="button" class="kidify-lbtn kidify-lbtn--primary" data-done>Done</button>
        </div>
      </div>`;
  };

  const close = () => {
    dialog.close();
    dialog.remove();
  };

  dialog.addEventListener('change', (event) => {
    const box = event.target.closest('input[data-list]');
    if (!box || !item) return;
    if (box.checked) lists.addTo(box.dataset.list, item);
    else lists.removeFrom(box.dataset.list, handle);
  });

  dialog.addEventListener('click', async (event) => {
    if (event.target.closest('[data-done]')) return close();
    if (event.target.closest('[data-new]')) {
      const name = await askName({ title: 'New list', label: 'List name', confirm: 'Create' });
      if (!name) return;
      const list = lists.create(name);
      if (list && item) lists.addTo(list.id, item);
      render();
    }
  });
  dialog.addEventListener('cancel', (event) => {
    event.preventDefault();
    close();
  });

  render();
  dialog.showModal();
}

/* ---------- Toast ---------- */

let toastTimer = 0;

/**
 * "Saved to <list> · Undo · Change". Undo takes the product back out of
 * every list (a heart only saves something that wasn't saved anywhere).
 * Save for later in the cart says "Moved to" and has no Undo: the line has
 * already left the cart.
 */
function showToast(handle, { undo = true, verb = 'Saved' } = {}) {
  let toast = document.querySelector('.kidify-ltoast');
  if (!toast) {
    toast = document.createElement('div');
    toast.className = 'kidify-ltoast';
    toast.setAttribute('role', 'status');
    document.body.append(toast);
  }
  const state = lists.all();
  const list = state.lists.find((l) => l.id === state.active);
  toast.innerHTML = `
    <span class="kidify-ltoast__text">${HEART}<span>${escapeHtml(verb)} to <strong>${escapeHtml(list?.name || 'Saved items')}</strong></span></span>
    ${undo ? '<button type="button" class="kidify-ltoast__undo" data-undo>Undo</button>' : ''}
    <button type="button" class="kidify-ltoast__change" data-change>Change</button>`;
  toast.querySelector('[data-change]').addEventListener('click', () => {
    toast.classList.remove('is-shown');
    openPicker(handle);
  });
  toast.querySelector('[data-undo]')?.addEventListener('click', () => {
    shelf.remove('saved', handle);
    track?.('remove', { handle, source: 'undo' });
    toast.classList.remove('is-shown');
  });
  toast.classList.add('is-shown');
  clearTimeout(toastTimer);
  const hide = () => toast.classList.remove('is-shown');
  toastTimer = setTimeout(hide, 5000);
  toast.onmouseenter = () => clearTimeout(toastTimer);
  toast.onmouseleave = () => (toastTimer = setTimeout(hide, 2500));
}

document.addEventListener(SAVED_EVENT, (event) => {
  if (event.detail?.handle) showToast(event.detail.handle, { undo: event.detail.undo !== false, verb: event.detail.verb || 'Saved' });
});

/* ---------- Share ---------- */

function shareUrl(list) {
  const url = new URL(SAVED_URL(), window.location.origin);
  url.searchParams.set('list', list.name);
  url.searchParams.set('items', list.items.map((entry) => entry.handle).join(','));
  return url.toString();
}

/* ---------- Lists page ---------- */

class KidifyLists extends HTMLElement {
  #current = null;
  #shared = null;

  connectedCallback() {
    this.root = this.querySelector('[data-lists-root]');
    const params = new URLSearchParams(window.location.search);
    if (params.get('items') !== null) {
      this.#shared = {
        name: (params.get('list') || 'Shared list').slice(0, 50),
        handles: params
          .get('items')
          .split(',')
          .map((h) => h.trim())
          .filter((h) => /^[a-z0-9][a-z0-9-]*$/i.test(h))
          .slice(0, 60),
      };
    }
    this.#current = lists.activeId();
    document.addEventListener(EVENT, this.#onChange);
    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#onSelect);
    this.#render();
  }

  disconnectedCallback() {
    document.removeEventListener(EVENT, this.#onChange);
  }

  #onChange = (event) => {
    if (event.detail?.kind === 'saved' && !this.#shared) this.#render();
  };

  #status(message) {
    const status = this.querySelector('[data-lists-status]');
    if (status) status.innerHTML = message;
  }

  async #render() {
    if (this.#shared) return this.#renderShared();
    const state = lists.all();
    if (!state.lists.some((list) => list.id === this.#current)) this.#current = state.active;
    const list = state.lists.find((l) => l.id === this.#current);
    const token = Symbol('render');
    this.token = token;

    this.root.innerHTML = `
      <div class="kidify-lists__layout">
        <nav class="kidify-lists__nav" aria-label="Your lists">
          <ul class="kidify-lists__tabs" role="list">
            ${state.lists
              .map(
                (l) => `<li><button type="button" class="kidify-lists__tab${l.id === list.id ? ' is-current' : ''}" data-open="${escapeHtml(l.id)}"${
                  l.id === list.id ? ' aria-current="true"' : ''
                }>
                  <span class="kidify-lists__tab-name">${escapeHtml(l.name)}</span>
                  <span class="kidify-lists__tab-meta">${l.id === state.active ? 'Default · ' : ''}${l.items.length}</span>
                </button></li>`
              )
              .join('')}
          </ul>
          <button type="button" class="kidify-lbtn kidify-lbtn--ghost kidify-lists__new" data-new>+ Create a list</button>
        </nav>
        <section class="kidify-lists__main" aria-labelledby="KidifyListTitle">
          <header class="kidify-lists__head">
            <div class="kidify-lists__title-row">
              <h2 class="kidify-lists__title" id="KidifyListTitle">${escapeHtml(list.name)}</h2>
              <span class="kidify-lists__count">${list.items.length} ${list.items.length === 1 ? 'item' : 'items'}${
                list.id === state.active ? ' · Default list' : ''
              }</span>
            </div>
            <div class="kidify-lists__actions">
              ${list.items.length ? '<button type="button" class="kidify-lbtn kidify-lbtn--primary kidify-lists__add-all" data-add-all>Add all to cart</button>' : ''}
              <button type="button" class="kidify-lbtn kidify-lbtn--ghost" data-share ${list.items.length ? '' : 'disabled'}>Share</button>
              <button type="button" class="kidify-lbtn kidify-lbtn--ghost" data-rename>Rename</button>
              ${list.id === state.active ? '' : '<button type="button" class="kidify-lbtn kidify-lbtn--ghost" data-default>Make default</button>'}
              ${state.lists.length > 1 ? '<button type="button" class="kidify-lbtn kidify-lbtn--ghost" data-delete>Delete list</button>' : ''}
            </div>
            <div class="kidify-lists__share" data-share-panel hidden></div>
            <p class="kidify-lists__status" role="status" data-lists-status></p>
          </header>
          <ul class="kidify-shelf__list kidify-shelf__list--grid kidify-lists__items" role="list" data-items></ul>
          <div class="kidify-lists__empty" ${list.items.length ? 'hidden' : ''}>
            <p>${escapeHtml(this.dataset.emptyText || 'Nothing in this list yet.')}</p>
            <a class="kidify-lbtn kidify-lbtn--primary" href="${escapeHtml(this.dataset.browseUrl || '/collections/all')}">${escapeHtml(
              this.dataset.browseLabel || 'Start browsing'
            )}</a>
          </div>
          <p class="kidify-lists__note">${escapeHtml(this.dataset.note || '')}</p>
        </section>
      </div>`;

    // On a phone the lists are a sideways row: bring the open one into view
    // (scrollLeft only, so the page itself never jumps).
    const nav = this.querySelector('.kidify-lists__nav');
    const tab = this.querySelector('.kidify-lists__tab.is-current');
    if (nav && tab && nav.scrollWidth > nav.clientWidth) {
      nav.scrollLeft = Math.max(0, tab.offsetLeft - nav.offsetLeft - 20);
    }

    const results = await Promise.all(list.items.map((entry) => fetchBoth(entry.handle).then((r) => ({ ...r, entry }))));
    if (this.token !== token) return;
    for (const r of results) if (r.gone) lists.removeFrom(list.id, r.entry.handle);
    const others = state.lists.filter((l) => l.id !== list.id);
    this.querySelector('[data-items]').innerHTML = results
      .filter((r) => !r.gone)
      .map((r) => this.#card(r, { listId: list.id, others }))
      .join('');

    // Items saved before prices were kept: today's price becomes the one a
    // later drop is measured from. One write, so one redraw.
    const baseline = results
      .filter((r) => r.product && !Number.isFinite(Number(r.entry.savedCents)))
      .map((r) => ({ handle: r.entry.handle, patch: { savedCents: r.product.price } }));
    if (baseline.length) lists.updateMany(list.id, baseline);
  }

  #card({ product, card, entry }, { listId, others = [], shared = false }) {
    const title = product?.title ?? entry.title;
    const url = product?.url ?? entry.url ?? `/products/${entry.handle}`;
    const { variant, reason, saved } = variantFor(product, entry);
    const handle = escapeHtml(entry.handle);
    let cartButton = '';
    if (variant) {
      cartButton = `<button type="button" class="kidify-lbtn kidify-lbtn--primary kidify-lists__cart" data-cart="${handle}">Add to cart</button>`;
    } else if (reason === 'options') {
      cartButton = `<a class="kidify-lbtn kidify-lbtn--primary kidify-lists__cart" href="${escapeHtml(url)}">Choose options</a>`;
    }
    // The option the shopper saved (edition, size), when the product has several.
    const savedOption =
      saved && product && !singleVariant(product)
        ? `<p class="kidify-lists__option">${escapeHtml(
            product.variants.find((v) => String(v.id) === String(entry.variant))?.title || ''
          )}${reason === 'soldout' ? ' · sold out' : ''}</p>`
        : '';
    const drop = shared ? null : priceDrop(product, entry);
    const dropped = drop
      ? `<p class="kidify-lists__drop"><span class="kidify-lists__drop-pill">Price dropped</span> was ${escapeHtml(money(drop.was))}</p>`
      : '';
    const note = shared
      ? ''
      : entry.note
        ? `<p class="kidify-lists__itemnote"><span>${escapeHtml(entry.note)}</span>
             <button type="button" class="kidify-lists__notebtn" data-note="${handle}" aria-label="Edit note for ${escapeHtml(title)}">Edit</button></p>`
        : `<button type="button" class="kidify-lists__notebtn" data-note="${handle}">Add a note</button>`;
    const move =
      !shared && others.length
        ? `<label class="kidify-lists__move"><span class="visually-hidden">Move ${escapeHtml(title)} to another list</span>
            <select data-move="${escapeHtml(entry.handle)}" data-from="${escapeHtml(listId)}">
              <option value="">Move to…</option>
              ${others.map((l) => `<option value="${escapeHtml(l.id)}">${escapeHtml(l.name)}</option>`).join('')}
            </select></label>`
        : '';
    const remove = shared
      ? ''
      : `<button type="button" class="kidify-lists__remove" data-remove="${escapeHtml(entry.handle)}" aria-label="Remove ${escapeHtml(
          title
        )} from this list">Remove</button>`;
    // The card itself is the product card template (CLAUDE.md), shared with
    // the shelves; this adds only the list's own buttons.
    return `<li class="kidify-shelf__item kidify-lists__item">
        ${miniCard({ card, entry: { ...entry, url } })}
        ${savedOption}${dropped}${note}
        <div class="kidify-lists__item-actions">${cartButton}${move}${remove}</div>
      </li>`;
  }

  async #renderShared() {
    const { name, handles } = this.#shared;
    this.root.innerHTML = `
      <div class="kidify-lists__shared">
        <header class="kidify-lists__head">
          <p class="kidify-lists__eyebrow">A list shared with you</p>
          <div class="kidify-lists__title-row">
            <h2 class="kidify-lists__title">${escapeHtml(name)}</h2>
            <span class="kidify-lists__count" data-shared-count>${handles.length} ${handles.length === 1 ? 'item' : 'items'}</span>
          </div>
          <div class="kidify-lists__actions">
            <button type="button" class="kidify-lbtn kidify-lbtn--primary" data-import ${handles.length ? '' : 'disabled'}>Save to my lists</button>
            <a class="kidify-lbtn kidify-lbtn--ghost" href="${escapeHtml(SAVED_URL())}">My lists</a>
          </div>
          <p class="kidify-lists__status" role="status" data-lists-status></p>
        </header>
        <ul class="kidify-shelf__list kidify-shelf__list--grid kidify-lists__items" role="list" data-items></ul>
      </div>`;
    const results = await Promise.all(handles.map((handle) => fetchBoth(handle).then((r) => ({ ...r, entry: { handle } }))));
    this.#shared.products = results.filter((r) => r.product).map((r) => r.product);
    this.querySelector('[data-items]').innerHTML = results
      .filter((r) => r.product)
      .map((r) => this.#card(r, { shared: true }))
      .join('');
    const found = this.#shared.products.length;
    const count = this.querySelector('[data-shared-count]');
    if (count) count.textContent = `${found} ${found === 1 ? 'item' : 'items'}`;
    const missing = results.filter((r) => !r.product).length;
    if (missing) this.#status(`${missing} ${missing === 1 ? 'item is' : 'items are'} no longer available.`);
  }

  #onSelect = (event) => {
    const select = event.target.closest('select[data-move]');
    if (!select || !select.value) return;
    const target = lists.get(select.value);
    lists.move(select.dataset.move, select.dataset.from, select.value);
    if (target) this.#status(`Moved to ${escapeHtml(target.name)}.`);
  };

  #onClick = async (event) => {
    const button = event.target.closest('button');
    if (!button) return;
    const state = lists.all();
    const list = state.lists.find((l) => l.id === this.#current);

    if (button.dataset.open) {
      this.#current = button.dataset.open;
      return this.#render();
    }
    if (button.hasAttribute('data-new')) {
      const name = await askName({ title: 'Create a list', label: 'List name, e.g. "Birthday wishes" or "Back to school"', confirm: 'Create' });
      if (!name) return;
      const created = lists.create(name);
      if (created) this.#current = created.id;
      else this.#status('You have the most lists allowed (20). Delete one to make room.');
      return this.#render();
    }
    if (button.hasAttribute('data-rename') && list) {
      const name = await askName({ title: 'Rename list', label: 'List name', value: list.name, confirm: 'Save' });
      if (name) lists.rename(list.id, name);
      return;
    }
    if (button.hasAttribute('data-default') && list) return lists.setActive(list.id);
    if (button.hasAttribute('data-delete') && list) {
      const ok = window.confirm(`Delete "${list.name}"? Its ${list.items.length} items will be removed from this list.`);
      if (ok) lists.remove(list.id);
      return;
    }
    if (button.dataset.remove && list) return lists.removeFrom(list.id, button.dataset.remove);
    if (button.hasAttribute('data-share') && list) return this.#share(list);
    if (button.dataset.note && list) {
      const entry = list.items.find((item) => item.handle === button.dataset.note);
      if (!entry) return;
      const note = await askName({
        title: entry.note ? 'Edit note' : 'Add a note',
        label: 'For example "Size 9" or "Jayden\'s birthday". Leave empty to remove it.',
        value: entry.note || '',
        confirm: 'Save',
        optional: true,
        max: 80,
      });
      if (note !== null) lists.update(list.id, entry.handle, { note: note.trim() });
      return;
    }
    if (button.hasAttribute('data-add-all') && list) return this.#addAll(list, button);
    if (button.dataset.cart) {
      const { product } = await fetchProduct(button.dataset.cart);
      if (!product) return;
      button.disabled = true;
      const entry = this.#shared ? null : list?.items.find((item) => item.handle === button.dataset.cart);
      const result = await addToCart(product, button, { entry, listName: this.#shared ? this.#shared.name : list?.name });
      button.disabled = false;
      this.#status(
        result.ok
          ? `Added ${escapeHtml(product.title)} to your cart. <a href="/cart">View cart</a>`
          : `${escapeHtml(result.message)}.`
      );
      return;
    }
    if (button.hasAttribute('data-import') && this.#shared) {
      const items = (this.#shared.products || []).map(snapshot);
      const created = lists.create(this.#shared.name, items.map((item) => ({ ...item, at: Date.now() })));
      this.#status(
        created
          ? `Saved as "${escapeHtml(created.name)}" in <a href="${escapeHtml(SAVED_URL())}">your lists</a>.`
          : 'You have the most lists allowed (20). Delete one to make room.'
      );
      button.disabled = Boolean(created);
    }
  };

  /**
   * Add all to cart: everything that can go straight in goes in one request,
   * then the status names what was left out (needs an option, sold out).
   * If the store refuses the batch (e.g. one item has too little stock),
   * the items are added one at a time so the rest still make it in.
   */
  async #addAll(list, button) {
    button.disabled = true;
    this.#status('Adding…');
    const results = await Promise.all(list.items.map((entry) => fetchProduct(entry.handle).then((r) => ({ ...r, entry }))));
    const ready = [];
    const needOption = [];
    const soldOut = [];
    for (const { product, entry } of results) {
      if (!product) continue;
      const { variant, reason } = variantFor(product, entry);
      if (variant) ready.push({ product, line: lineFor(variant, list.name) });
      else if (reason === 'options') needOption.push(product);
      else soldOut.push(product);
    }

    let added = [];
    const failed = [];
    if (ready.length) {
      const batch = await postLines(
        ready.map((r) => r.line),
        button
      );
      if (batch.ok) added = ready;
      else {
        for (const r of ready) {
          const one = await postLines([r.line], button);
          (one.ok ? added : failed).push(r);
        }
      }
    }
    if (added.length) track?.('to_cart', { list: list.name, count: added.length, source: 'add_all' });

    const names = (items) => items.map((i) => escapeHtml(i.product?.title ?? i.title)).join(', ');
    const parts = [];
    if (added.length) parts.push(`Added ${added.length} ${added.length === 1 ? 'item' : 'items'} to your cart. <a href="/cart">View cart</a>`);
    if (needOption.length) parts.push(`Choose an option for: ${names(needOption)}.`);
    if (soldOut.length) parts.push(`Sold out: ${names(soldOut)}.`);
    if (failed.length) parts.push(`Couldn't add: ${names(failed)}.`);
    this.#status(parts.join(' ') || 'Nothing in this list can be added right now.');
    button.disabled = false;
  }

  async #share(list) {
    const url = shareUrl(list);
    const text = `${list.name} – my Kidify list`;
    if (navigator.share) {
      try {
        await navigator.share({ title: text, text, url });
        return;
      } catch {
        /* closed the share sheet, or not allowed: fall back to the panel */
      }
    }
    const panel = this.querySelector('[data-share-panel]');
    panel.innerHTML = `
      <p class="kidify-lists__share-note">Anyone with this link can see this list and add its items to their cart. It shows the list as it is now.</p>
      <div class="kidify-lists__share-row">
        <input class="kidify-lists__share-url" readonly value="${escapeHtml(url)}" aria-label="Share link">
        <button type="button" class="kidify-lbtn kidify-lbtn--primary" data-copy>Copy link</button>
        <a class="kidify-lbtn kidify-lbtn--ghost" target="_blank" rel="noopener" href="https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}">WhatsApp</a>
      </div>`;
    panel.hidden = false;
    panel.querySelector('[data-copy]').addEventListener('click', async () => {
      const input = panel.querySelector('input');
      try {
        await navigator.clipboard.writeText(url);
      } catch {
        input.select();
        document.execCommand?.('copy');
      }
      this.#status('Link copied.');
    });
  }
}

if (lists && !customElements.get('kidify-lists')) customElements.define('kidify-lists', KidifyLists);
